export type UserStatus = 'Waiting setup' | 'Free trial' | 'Active' | 'Cancelled'
export type PaymentStatus =
  | 'Pending'
  | 'Submitted'
  | 'Confirmed'
  | 'Paid out'
  | 'Failed'

export type Company = {
  name: string
  number: string
  address: string
  status: string
  incorporated: string
  sicCodes: string[]
}

export type Payment = {
  id: string
  userId: string
  date: string
  amount: number
  status: PaymentStatus
}

export type LifecycleEventKind =
  | 'signed_up'
  | 'satago_added'
  | 'trial_expired'
  | 'cancelled'

export type LifecycleEvent = {
  id: string
  userId: string
  kind: LifecycleEventKind
  date: string
}

export type Customer = {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  businessType: 'Company' | 'Sole trader'
  company?: Company
  tradingName?: string
  address: string
  status: UserStatus
  trialExpiry?: string
  subscription: string
  planName: string
  planPrice: number
  planDiscount?: string
}

export const companies: Company[] = [
  {
    name: 'Northline Studio Ltd',
    number: '14289310',
    address: 'Unit 4, 75 Curtain Road, London, EC2A 3BS',
    status: 'Active',
    incorporated: '14 July 2022',
    sicCodes: [
      '74100 — Specialised design activities',
      '62012 — Business software development',
    ],
  },
  {
    name: 'Northline Logistics Limited',
    number: '11804562',
    address: '36 Union Street, Manchester, M12 4JD',
    status: 'Active',
    incorporated: '28 January 2019',
    sicCodes: ['49410 — Freight transport by road'],
  },
  {
    name: 'North Star Ventures Ltd',
    number: '15572004',
    address: '8 Brindley Place, Birmingham, B1 2JB',
    status: 'Active',
    incorporated: '7 March 2024',
    sicCodes: ['70229 — Management consultancy activities'],
  },
]

export const seededUsers: Customer[] = [
  {
    id: 'usr_001',
    firstName: 'Amelia',
    lastName: 'Carter',
    email: 'amelia@northline.studio',
    phone: '07700 900 421',
    businessType: 'Company',
    company: companies[0],
    address: companies[0].address,
    status: 'Active',
    trialExpiry: '12 May 2026',
    subscription: 'ADVANCED · £60 / month',
    planName: 'ADVANCED',
    planPrice: 60,
  },
  {
    id: 'usr_002',
    firstName: 'Marcus',
    lastName: 'Reed',
    email: 'marcus@ledgerandco.uk',
    phone: '07700 900 587',
    businessType: 'Sole trader',
    tradingName: 'Ledger & Co Bookkeeping',
    address: '19 Dean Street, Bristol, BS1 5AL',
    status: 'Free trial',
    trialExpiry: '02 August 2026',
    subscription: 'Trial · 20 days remaining',
    planName: 'ESSENTIAL',
    planPrice: 40,
  },
  {
    id: 'usr_003',
    firstName: 'Priya',
    lastName: 'Shah',
    email: 'priya@northlinelogistics.co.uk',
    phone: '07700 900 204',
    businessType: 'Company',
    company: companies[1],
    address: companies[1].address,
    status: 'Waiting setup',
    subscription: 'Not started',
    planName: 'ADVANCED',
    planPrice: 40.02,
    planDiscount: 'FORE Business 33.3% off',
  },
  {
    id: 'usr_004',
    firstName: 'Daniel',
    lastName: 'Owens',
    email: 'daniel@owensworks.co.uk',
    phone: '07700 900 962',
    businessType: 'Sole trader',
    tradingName: 'Owens Works',
    address: '42 Merton Lane, Leeds, LS6 1AR',
    status: 'Cancelled',
    subscription: 'Cancelled',
    planName: 'STARTER',
    planPrice: 20,
  },
  {
    id: 'usr_005',
    firstName: 'Jade',
    lastName: 'Walker',
    email: 'jade@northstarventures.co.uk',
    phone: '07700 900 310',
    businessType: 'Company',
    company: companies[2],
    address: companies[2].address,
    status: 'Free trial',
    trialExpiry: '08 July 2026',
    subscription: 'Trial · expired',
    planName: 'ENTERPRISE',
    planPrice: 120,
  },
]

export const seededLifecycleEvents: LifecycleEvent[] = [
  {
    id: 'evt_amelia_signup',
    userId: 'usr_001',
    kind: 'signed_up',
    date: '12 Apr 2026',
  },
  {
    id: 'evt_amelia_satago',
    userId: 'usr_001',
    kind: 'satago_added',
    date: '13 Apr 2026',
  },
  {
    id: 'evt_marcus_signup',
    userId: 'usr_002',
    kind: 'signed_up',
    date: '09 Jul 2026',
  },
  {
    id: 'evt_marcus_satago',
    userId: 'usr_002',
    kind: 'satago_added',
    date: '10 Jul 2026',
  },
  {
    id: 'evt_priya_signup',
    userId: 'usr_003',
    kind: 'signed_up',
    date: '11 Jul 2026',
  },
  {
    id: 'evt_daniel_signup',
    userId: 'usr_004',
    kind: 'signed_up',
    date: '03 Apr 2026',
  },
  {
    id: 'evt_daniel_satago',
    userId: 'usr_004',
    kind: 'satago_added',
    date: '04 Apr 2026',
  },
  {
    id: 'evt_daniel_cancelled',
    userId: 'usr_004',
    kind: 'cancelled',
    date: '26 Jun 2026',
  },
  {
    id: 'evt_jade_signup',
    userId: 'usr_005',
    kind: 'signed_up',
    date: '08 Jun 2026',
  },
  {
    id: 'evt_jade_satago',
    userId: 'usr_005',
    kind: 'satago_added',
    date: '09 Jun 2026',
  },
  {
    id: 'evt_jade_trial_expired',
    userId: 'usr_005',
    kind: 'trial_expired',
    date: '08 Jul 2026',
  },
]

export const seededPayments: Payment[] = [
  {
    id: 'pay_1842',
    userId: 'usr_001',
    date: '10 Jul 2026',
    amount: 49,
    status: 'Paid out',
  },
  {
    id: 'pay_1831',
    userId: 'usr_001',
    date: '10 Jun 2026',
    amount: 49,
    status: 'Paid out',
  },
  {
    id: 'pay_1851',
    userId: 'usr_005',
    date: '12 Jul 2026',
    amount: 49,
    status: 'Confirmed',
  },
  {
    id: 'pay_1847',
    userId: 'usr_002',
    date: '09 Jul 2026',
    amount: 49,
    status: 'Submitted',
  },
  {
    id: 'pay_1854',
    userId: 'usr_001',
    date: '12 Jul 2026',
    amount: 49,
    status: 'Failed',
  },
  {
    id: 'pay_1798',
    userId: 'usr_001',
    date: '10 May 2026',
    amount: 49,
    status: 'Paid out',
  },
]
